import React, { useState } from 'react';
import { AlertTriangle, Shield, Phone, Heart, MessageCircle, CheckCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const CrisisDetection = ({ message, onComplete, standalone = false }) => {
  const [checking, setChecking] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [showResources, setShowResources] = useState(false);

  const checkCrisisRisk = async (textToCheck) => {
    setChecking(true);
    try {
      const response = await axios.post(`${API}/mental-health/crisis-check`, {
        message: textToCheck,
        check_history: true
      });
      
      setAssessment(response.data);
      
      if (response.data.risk_level === 'critical' || response.data.risk_level === 'high') {
        setShowResources(true);
        
        if (response.data.risk_level === 'critical') {
          toast.error('Crisis detected - Please seek immediate help', { duration: 10000 });
        } else {
          toast.warning('High risk detected - Support resources provided', { duration: 8000 });
        }
      }
      
      if (onComplete) {
        onComplete(response.data);
      }
      
    } catch (error) {
      console.error('Error checking crisis risk:', error);
      toast.error('Unable to perform crisis assessment');
    } finally {
      setChecking(false);
    }
  };

  React.useEffect(() => {
    if (message && !assessment) {
      checkCrisisRisk(message);
    }
  }, [message]);

  if (!assessment && !checking) {
    return null;
  }

  if (checking) {
    return (
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span className="text-sm text-blue-700">Analyzing message for wellbeing...</span>
        </div>
      </div>
    );
  }

  const getRiskColor = (level) => {
    switch (level) {
      case 'critical': return 'red';
      case 'high': return 'orange';
      case 'medium': return 'yellow';
      default: return 'green';
    }
  };

  const color = getRiskColor(assessment.risk_level);

  return (
    <div className={`border-2 rounded-lg p-6 bg-${color}-50 border-${color}-200`}>
      {/* Risk Level Indicator */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {assessment.risk_level === 'critical' || assessment.risk_level === 'high' ? (
            <AlertTriangle className={`w-8 h-8 text-${color}-600`} />
          ) : assessment.risk_level === 'medium' ? (
            <Shield className={`w-8 h-8 text-${color}-600`} />
          ) : (
            <CheckCircle className={`w-8 h-8 text-${color}-600`} />
          )}
          <div>
            <h3 className={`text-xl font-bold text-${color}-800`}>
              {assessment.risk_level === 'critical' && 'Critical - Immediate Support Needed'}
              {assessment.risk_level === 'high' && 'High Priority - Please Seek Support'}
              {assessment.risk_level === 'medium' && 'Moderate Concern - Support Available'}
              {assessment.risk_level === 'low' && 'You\'re Doing Well'}
            </h3>
            <p className={`text-sm text-${color}-600 mt-1`}>
              Support Level: {assessment.risk_level.charAt(0).toUpperCase() + assessment.risk_level.slice(1)}
            </p>
          </div>
        </div>
      </div>

      {/* Recommended Action */}
      <div className={`bg-white p-4 rounded-lg border border-${color}-200 mb-4`}>
        <h4 className="font-semibold text-gray-800 mb-2">Recommended Action:</h4>
        <p className="text-gray-700">{assessment.recommended_action}</p>
      </div>

      {/* Crisis Resources - Show for Medium and above */}
      {(assessment.risk_level !== 'low') && assessment.crisis_resources && (
        <div className="space-y-4">
          <h4 className="font-bold text-gray-800 flex items-center gap-2">
            <Phone className="w-5 h-5" />
            Crisis Support Resources
          </h4>
          
          {assessment.crisis_resources.map((resource, index) => (
            <Card key={`item-${index}`} className="p-4 bg-white">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h5 className="font-semibold text-gray-800">{resource.name}</h5>
                  <p className="text-sm text-gray-600 mt-1">{resource.description}</p>
                  <div className="mt-2 space-y-1">
                    <p className="text-sm">
                      <span className="font-medium">Phone:</span> 
                      <a href={`tel:${resource.phone}`} className="text-teal-600 ml-2 hover:underline">
                        {resource.phone}
                      </a>
                    </p>
                    {resource.email && (
                      <p className="text-sm">
                        <span className="font-medium">Email:</span> 
                        <a href={`mailto:${resource.email}`} className="text-teal-600 ml-2 hover:underline">
                          {resource.email}
                        </a>
                      </p>
                    )}
                    <p className="text-sm">
                      <span className="font-medium">Available:</span> 
                      <span className="ml-2">{resource.available}</span>
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => window.open(`tel:${resource.phone}`, '_self')}
                  className="ml-4"
                >
                  <Phone className="w-4 h-4 mr-1" />
                  Call Now
                </Button>
              </div>
            </Card>
          ))}

          {/* Emergency Services */}
          <Card className="p-4 bg-red-50 border-red-200">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
              <div className="flex-1">
                <h5 className="font-semibold text-red-800">Emergency Services</h5>
                <p className="text-sm text-red-700 mt-1">
                  If you're in immediate danger, call <strong>112</strong> (India Emergency Number)
                </p>
                <Button 
                  size="sm" 
                  className="mt-3 bg-red-600 hover:bg-red-700"
                  onClick={() => window.open('tel:112', '_self')}
                >
                  <Phone className="w-4 h-4 mr-1" />
                  Call 112 Now
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Positive Reinforcement for Low Risk */}
      {assessment.risk_level === 'low' && (
        <div className="bg-white p-4 rounded-lg border border-green-200">
          <div className="flex items-center gap-3">
            <Heart className="w-6 h-6 text-green-600" />
            <div>
              <p className="text-gray-700">
                It's great that you're checking in on your mental health. 
                Keep up the self-care practices and don't hesitate to reach out if you need support.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Additional Help */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <p className="text-sm text-gray-600 mb-3">
          <strong>Remember:</strong> You're not alone. Mental health professionals are here to help.
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.location.href = '/ai-features?tab=mental-health'}
          >
            <MessageCircle className="w-4 h-4 mr-1" />
            Continue to Mental Health Support
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              // Could integrate with appointments or other features
              toast.info('This feature will be connected to professional help booking');
            }}
          >
            <Phone className="w-4 h-4 mr-1" />
            Book Professional Consultation
          </Button>
        </div>
      </div>

      {/* Privacy Notice */}
      <p className="text-xs text-gray-500 mt-4 italic">
        🔒 Your mental health information is confidential and secure. 
        Crisis assessments are used only to provide you with appropriate support resources.
      </p>
    </div>
  );
};

export default CrisisDetection;
